import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { ApplicationState } from "../../store";
import * as AccessControlGroupsState from "../../store/AccessControlGroups";
import { AccessControlGroupType } from "../../api/ApiClient";
import * as Auth0State from "../../store/Auth0";
import { getExternalTenantId } from "../../helpers";
import { MeState } from "../../store/Me";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Button } from "@mui/material";

type RegisterOwnProps = {
  onRegistered: () => any;
  initialValues?: RegisterAccessControlGroupParams;
};

export interface RegisterAccessControlGroupParams {
  //create accessControlGroup

  externalId: string;
  tenantExternalId: string;
  propertyExternalId: string;
  unitNumber: string;
  type: AccessControlGroupType;
  name: string;
  description: string;
}

const Register: React.FC<RegisterOwnProps> = ({
  onRegistered,
  initialValues,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const auth0 = useSelector((state: ApplicationState) => state.auth0);
  const me = useSelector((state: ApplicationState) => state.me);

  const [formState, setFormState] = useState<
    RegisterAccessControlGroupParams & { inProgress: boolean }
  >({
    inProgress: false,
    ...(initialValues || {
      //create accessControlGroup
      externalId: "",
      tenantExternalId: "",
      propertyExternalId: "",
      unitNumber: "",
      type: AccessControlGroupType.Regular,
      name: "",
      description: "",
    }),
  });

  const register = async (e: React.FormEvent) => {
    e.preventDefault();

    setFormState((prev) => ({ ...prev, inProgress: true }));

    try {
      const json = await (dispatch as any)(
        AccessControlGroupsState.actionCreators.registerAccessControlGroup({
          externalId: formState.externalId,
          tenantExternalId: getExternalTenantId(me.user),
          propertyExternalId: formState.propertyExternalId,
          unitNumber: formState.unitNumber,
          accessControlGroupType: formState.type,
          name: formState.name,
          description: formState.description,
        }),
      );

      alert(
        "Successfully registered accessControlGroup: " + JSON.stringify(json),
      );

      onRegistered();

      setFormState({
        //clear inputs
        inProgress: false,
        externalId: "",
        tenantExternalId: "",
        propertyExternalId: "",
        unitNumber: "",
        type: AccessControlGroupType.Regular,
        name: "",
        description: "",
      });
    } catch (e) {
      // Extract the specific error message from the nested response
      let errorMessage = "An unexpected error occurred";
      try {
        const errorData = e as any;
        if (errorData?.response) {
          const responseData = JSON.parse(errorData.response);
          if (responseData?.errors && responseData.errors.length > 0) {
            // Map all error messages and join them
            errorMessage = responseData.errors
              .map((error: any) => error.message)
              .join(", ");
          } else if (responseData?.message) {
            errorMessage = responseData.message;
          }
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If parsing fails, use the original message or fallback
        errorMessage = (e as any)?.message || "An unexpected error occurred";
      }

      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          {errorMessage}
        </p>,
        {
          variant: "error",
        },
      );
    } finally {
      setFormState((prev) => ({ ...prev, inProgress: false }));
    }
  };

  const { inProgress, externalId, type, propertyExternalId, unitNumber, name } =
    formState;

  return (
    <form>
      <div className="form-group">
        <label htmlFor="externalId">Unique Name *</label>
        <input
          className="form-control"
          name="externalId"
          placeholder="Unique Name"
          value={externalId}
          onChange={(e) => {
            setFormState((prev) => ({ ...prev, externalId: e.target.value }));
          }}
        />
        <p className="help-block">
          Enter any unique id you want, e.g. "building1_common_doors_1"
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="name">Name *</label>
        <input
          className="form-control"
          name="name"
          placeholder="Name"
          value={name}
          onChange={(e) => {
            setFormState((prev) => ({ ...prev, name: e.target.value }));
          }}
        />
      </div>

      <div className="form-group">
        <label htmlFor="systemType">Group Type *</label>
        <select
          className="form-control"
          name="groupType"
          value={type}
          onChange={(e) => {
            setFormState((prev) => ({ ...prev, type: e.target.value as any }));
          }}
        >
          <option value="">Select Group Type</option>
          <option value="regular">Regular</option>
          <option value="defaultForProperty">Default For Property</option>
          <option value="unit">Unit</option>
          <option value="defaultForGuests">Default For Guests</option>
          <option value="defaultForStaff">Default For Staff</option>
          <option value="staffOnly">Staff Only</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="propertyExternalId">
          Property Unique Name (optional)
        </label>
        <input
          className="form-control"
          name="propertyExternalId"
          placeholder="Property Unique Name"
          value={propertyExternalId}
          onChange={(e) => {
            setFormState((prev) => ({
              ...prev,
              propertyExternalId: e.target.value,
            }));
          }}
        />
        <p className="help-block">
          Enter property id matching external inbound system e.g. Livly
          propertyCode
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="unitNumber">Unit Number (optional)</label>
        <input
          className="form-control"
          name="unitNumber"
          placeholder="Unit Number"
          value={unitNumber}
          onChange={(e) => {
            setFormState((prev) => ({ ...prev, unitNumber: e.target.value }));
          }}
        />
        <p className="help-block">
          Unit Number matching external inbound system
        </p>
      </div>

      <div>
        <Button
          type="submit"
          disabled={!!inProgress}
          variant="contained"
          onClick={register}
        >
          Register AccessControlGroup
        </Button>
      </div>
    </form>
  );
};

export default Register;
